import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BackArrowIcon, CheckIcon, SpinnerIcon } from "../Login/components/AuthIcons";
import { fetchReverseGeocode, shortenLocation } from "./locationUtils";

// Minimal map-based location selector.
//
// Backend reality (read-only — verified in Tasmeh-backend):
// there is NO Neshan/maps endpoint, no map API key, and no lat/lng columns.
// The Mechanic model carries location only as the free-text `address` field
// (POST /mechanics/register, PUT /mechanics/me). So this picker is a pure
// frontend selector: the user taps a point on keyless OpenStreetMap tiles
// (no key, nothing secret to expose) and the confirmed coordinates are
// handed back to the registration form, which submits them inside the
// existing `address` string via the existing frontend → backend flow.
// Nothing here is hardcoded: the saved point always comes from an explicit
// user tap (or the device geolocation fix the user accepts).
const FALLBACK_VIEW = { lat: 35.6892, lng: 51.389, zoom: 12 }; // Tehran viewport only

function MapPicker({ open, initial, onConfirm, onClose }) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");
  // Short human-readable summary of the draft point, built from the REAL
  // reverse-lookup response. Raw coordinates are kept internal-only and are
  // never displayed to the user.
  const [pendingLabel, setPendingLabel] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);
  const lookupSeq = useRef(0);

  // Reset the draft selection every time the picker opens.
  useEffect(() => {
    if (open) {
      lookupSeq.current += 1;
      setPending(initial ? { lat: initial.lat, lng: initial.lng } : null);
      setPendingLabel("");
      setLabelLoading(false);
      setGeoError("");
      setLocating(false);
    }
  }, [open, initial]);

  // Resolve the short summary for the draft point (debounced, race-safe).
  useEffect(() => {
    if (!open || !pending) {
      setPendingLabel("");
      setLabelLoading(false);
      return;
    }
    const seq = lookupSeq.current + 1;
    lookupSeq.current = seq;
    setLabelLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const data = await fetchReverseGeocode(pending.lat, pending.lng);
        if (lookupSeq.current !== seq) return;
        setPendingLabel(shortenLocation(data));
      } finally {
        if (lookupSeq.current === seq) setLabelLoading(false);
      }
    }, 450);
    return () => window.clearTimeout(timer);
  }, [open, pending]);

  // Build the Leaflet map once the modal DOM exists.
  useEffect(() => {
    if (!open || !mapNodeRef.current || mapRef.current) return;

    const start = initial || FALLBACK_VIEW;
    const map = L.map(mapNodeRef.current, {
      center: [start.lat, start.lng],
      zoom: initial ? 15 : FALLBACK_VIEW.zoom,
      zoomControl: true,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    const placeMarker = (lat, lng) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      } else {
        markerRef.current = L.circleMarker([lat, lng], {
          radius: 10,
          color: "#142642",
          weight: 3,
          fillColor: "#0f6fb4",
          fillOpacity: 0.85,
        }).addTo(map);
      }
    };

    if (initial) placeMarker(initial.lat, initial.lng);

    const onTap = (ev) => {
      const { lat, lng } = ev.latlng;
      placeMarker(lat, lng);
      setPending({ lat, lng });
      setGeoError("");
    };
    map.on("click", onTap);

    // Let the modal finish layout before Leaflet measures its container.
    const fixSize = window.setTimeout(() => map.invalidateSize(), 120);

    return () => {
      window.clearTimeout(fixSize);
      map.off("click", onTap);
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // Re-create per opening; `initial` is captured intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open ]);

  // Lock page scroll + close on Escape while the picker is open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (ev) => {
      if (ev.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleLocateMe = () => {
    if (!("geolocation" in navigator)) {
      setGeoError("موقعیت‌یاب روی این دستگاه پشتیبانی نمی‌شود");
      return;
    }
    setLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setLocating(false);
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 15, { animate: true });
          if (markerRef.current) {
            markerRef.current.setLatLng([lat, lng]);
          } else {
            markerRef.current = L.circleMarker([lat, lng], {
              radius: 10,
              color: "#142642",
              weight: 3,
              fillColor: "#0f6fb4",
              fillOpacity: 0.85,
            }).addTo(mapRef.current);
          }
        }
        setPending({ lat, lng });
      },
      () => {
        setLocating(false);
        setGeoError("دسترسی به موقعیت مکانی داده نشد؛ روی نقشه بزنید");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div
      className="swr-map-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="انتخاب موقعیت روی نقشه"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose?.();
      }}
    >
      <div className="swr-map-panel">
        <div className="swr-map-head">
          <button
            className="swr-map-close"
            type="button"
            onClick={onClose}
            aria-label="بستن نقشه"
          >
            <BackArrowIcon size={20} />
          </button>
          <h2 className="swr-map-title">انتخاب موقعیت روی نقشه</h2>
          <span className="swr-map-head-spacer" aria-hidden="true" />
        </div>

        <p className="swr-map-hint">روی نقشه بزنید تا موقعیت مجموعه مشخص شود</p>

        <div ref={mapNodeRef} className="swr-map-view" />

        <p className="swr-map-coords-fa" aria-live="polite">
          {pending
            ? labelLoading
              ? "در حال دریافت آدرس..."
              : pendingLabel || "موقعیت انتخاب شد"
            : "هنوز موقعیتی انتخاب نشده است"}
        </p>

        {geoError && (
          <p className="swr-map-error" role="alert">{geoError}</p>
        )}

        <div className="swr-map-actions">
          <button
            className="auth-btn auth-btn-primary swr-map-confirm"
            type="button"
            disabled={!pending}
            onClick={() => pending && onConfirm?.(pending)}
          >
            <CheckIcon size={18} />
            تایید موقعیت
          </button>
          <button
            className="auth-btn auth-btn-ghost swr-map-ghost"
            type="button"
            onClick={handleLocateMe}
            disabled={locating}
          >
            {locating && <SpinnerIcon size={18} />}
            {locating ? "در حال یافتن..." : "موقعیت من"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MapPicker;
